/**
 * Season data client — the Feed Protocol ladder for every editorial surface:
 *
 *   public feed (GET /api/feeds/<feed>)
 *     → last-good localStorage snapshot
 *       → bundled season seed (src/season/*.json, baked at build time)
 *
 * The hook reports provenance with the data: `asOf` (envelope updatedAt) and
 * `stale` (past the envelope's staleAfter, or seed-only), so screens can show
 * honest "as of" labels instead of silently lying about freshness.
 */
import { ReadUnavailable, resetPublicReads, retryAfterMs, usePublicRead } from './public-reads';
import type { ClubFeed, CodexFeed, CommunityReport, FixturesFeed, GlossaryFeed, LoreFeed, MatchFeed, NewsFeed, NewsItem, Player, PlayerStory, SquadFeed } from './types';
import seedFixtures from '../season/fixtures.json';
import seedMatch from '../season/match.json';
import seedSquad from '../season/squad.json';
import seedNews from '../season/news.json';
import seedClub from '../season/club.json';
import seedCodex from '../season/codex.json';
import seedLore from '../season/lore.json';
import seedGlossary from '../season/glossary.json';
import { STANDALONE_DATA_EPOCH } from './release';

export const APP = 'coyg';
export interface FeedEnvelope<T = unknown> {
  app: string;
  feed: string;
  schema: string;
  version: number;
  updatedAt: string;
  staleAfter: string | null;
  payload: T;
}

// The standalone epoch is part of the cache namespace. Cannon-era or earlier
// COYG snapshots can never become first-paint truth after the clean cutover.
export const feedKey = (feed: string) => `coyg:${STANDALONE_DATA_EPOCH}:feed:${feed}`;
const RIGHTS_GATED_FEEDS = new Set(['fixtures', 'match-live']);

export function londonCalendarDate(at = Date.now()): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date(at));
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

export function isCurrentLondonEdition(date: string | undefined, at = Date.now()): boolean {
  return date === londonCalendarDate(at);
}
function cachedFeed<T>(spec: SeedSpec<T>): FeedEnvelope<T> | null {
  try {
    if (RIGHTS_GATED_FEEDS.has(spec.feed)) {
      localStorage.removeItem(feedKey(spec.feed));
      return null;
    }
    const raw = localStorage.getItem(feedKey(spec.feed));
    if (!raw) return null;
    const envelope = JSON.parse(raw) as FeedEnvelope<T>;
    if (!validEnvelope(envelope, spec)) {
      localStorage.removeItem(feedKey(spec.feed));
      return null;
    }
    return envelope;
  } catch { return null; }
}

async function getFeed<T>(spec: SeedSpec<T>): Promise<FeedEnvelope<T> | null> {
  const response = await fetch(`/api/feeds/${encodeURIComponent(spec.feed)}`, { cache: 'no-store', signal: AbortSignal.timeout(15_000) });
  if (response.status === 404) return null;
  if (!response.ok) throw new ReadUnavailable(response.status, retryAfterMs(response.headers.get('retry-after')));
  const envelope = await response.json() as FeedEnvelope<T>;
  if (!validEnvelope(envelope, spec)) throw new Error('feed-invalid');
  if (!RIGHTS_GATED_FEEDS.has(spec.feed)) {
    try { localStorage.setItem(feedKey(spec.feed), JSON.stringify(envelope)); } catch { /* best effort */ }
  }
  return envelope;
}

export interface FeedState<T> {
  data: T;
  /** ISO timestamp of the snapshot, null when running on the bundled seed. */
  asOf: string | null;
  /** True when past staleAfter or when only the bundled seed is available. */
  stale: boolean;
  /** True once a live fetch has succeeded this session. */
  online: boolean;
}

interface SeedSpec<T> {
  feed: string;
  seed: T;
  mergeSeed?: (live: T, seed: T) => T;
  validate?: (payload: unknown) => payload is T;
}

function validEnvelope<T>(envelope: FeedEnvelope<T>, spec: SeedSpec<T>): boolean {
  if (!envelope || typeof envelope !== 'object') return false;
  const legacy = envelope as FeedEnvelope<T> & { sequence?: unknown };
  const revision = Number.isSafeInteger(envelope.version) && envelope.version >= 1
    ? envelope.version
    : Number.isSafeInteger(legacy.sequence) && Number(legacy.sequence) >= 1 ? Number(legacy.sequence) : null;
  return Boolean(envelope.app === APP && envelope.feed === spec.feed &&
    (envelope.schema == null || (typeof envelope.schema === 'string' && envelope.schema.length > 0)) && revision !== null &&
    typeof envelope.updatedAt === 'string' && Number.isFinite(Date.parse(envelope.updatedAt)) &&
    (spec.feed === 'match-live'
      ? typeof envelope.staleAfter === 'string' && Number.isFinite(Date.parse(envelope.staleAfter))
      : envelope.staleAfter === null || (typeof envelope.staleAfter === 'string' && Number.isFinite(Date.parse(envelope.staleAfter)))) &&
    (!spec.validate || spec.validate(envelope.payload)));
}

const norm = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim();
// Normalise a source identifier to a brand token.
// 'BBC' and 'bbc.co.uk' both → 'bbc' (domain-like strings have their dotted
// suffix stripped); plain names are just lowercased, so 'BBC Sport' → 'bbc sport'
// and 'Guardian' → 'guardian' (distinct brands stay distinct).
const normBrand = (source: string): string => source.toLowerCase().replace(/(\.\w+)+$/, '').trim();
const sourcePublisher = (url: string): string => {
  const host = new URL(url).hostname.replace(/^www\./, '').toLowerCase();
  const parts = host.split('.');
  if (parts.length <= 2) return host;
  const suffix = parts.slice(-2).join('.');
  return ['co.uk', 'org.uk', 'ac.uk', 'gov.uk'].includes(suffix) ? parts.slice(-3).join('.') : suffix;
};

/**
 * Stable dedup key for a headline: normalised title + brand so 'BBC' and
 * 'bbc.co.uk' collapse to the same token while 'BBC' and 'Guardian' stay
 * distinct. Shared by the news-feed dedup and the Today-report dedup so both
 * surfaces collapse the same duplicate headlines.
 */
export const headlineKey = (title: string, source: string): string =>
  `${norm(title)}::${normBrand(source)}`;

export function dedupeNewsItems(items: NewsItem[]): NewsItem[] {
  const seen = new Set<string>();
  const out: NewsItem[] = [];
  for (const it of Array.isArray(items) ? items : []) {
    if (!it || typeof it !== 'object' || typeof it.title !== 'string' || typeof it.source !== 'string' ||
      typeof it.summary !== 'string' || typeof it.publishedAt !== 'string' || !Number.isFinite(Date.parse(it.publishedAt)) ||
      typeof it.url !== 'string') continue;
    try { if (new URL(it.url).protocol !== 'https:') continue; } catch { continue; }
    if (it.sourceUrls != null) {
      try {
        if (!Array.isArray(it.sourceUrls) || it.sourceUrls.length === 0 || new Set(it.sourceUrls).size !== it.sourceUrls.length ||
            !it.sourceUrls.every((url) => new URL(url).protocol === 'https:') || !it.sourceUrls.includes(it.url)) continue;
      } catch { continue; }
    }
    if (it.sources == null ? it.sourceUrls != null : !Number.isInteger(it.sources) || it.sources <= 0 ||
        (it.sources === 1 ? it.sourceUrls != null && it.sourceUrls.length !== 1 : !it.sourceUrls || it.sourceUrls.length !== it.sources)) continue;
    if (it.verified === true && (!(it.sources && it.sources >= 2) || !it.sourceUrls || it.sourceUrls.length !== it.sources ||
        new Set(it.sourceUrls.map(sourcePublisher)).size !== it.sourceUrls.length)) continue;
    const key = headlineKey(it.title, it.source);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(it);
  }
  return out;
}

function hasTodayDispatch(feed: NewsFeed): boolean {
  const today = feed.today;
  const validDate = (value: unknown): boolean => {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const parsed = new Date(`${value}T00:00:00Z`);
    return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
  };
  const validSource = (item: unknown): boolean => {
    if (!item || typeof item !== 'object') return false;
    const candidate = item as { title?: unknown; summary?: unknown; source?: unknown; sourceUrl?: unknown; sourceUrls?: unknown; sources?: unknown; category?: unknown; certainty?: unknown; discussionRoom?: unknown; publishedAt?: unknown; related?: unknown };
    if (typeof candidate.title !== 'string' || candidate.title.trim().length === 0 || typeof candidate.summary !== 'string' || candidate.summary.trim().length === 0 || typeof candidate.source !== 'string' || candidate.source.trim().length === 0 || typeof candidate.sourceUrl !== 'string' ||
      !['lead', 'players', 'transfers', 'club', 'match', 'international'].includes(String(candidate.category)) ||
      !['official', 'verified', 'reported', 'developing', 'unconfirmed'].includes(String(candidate.certainty)) ||
      !['match', 'TRANSFERS', 'SEASON', 'ANALYSIS', 'all'].includes(String(candidate.discussionRoom)) ||
      typeof candidate.publishedAt !== 'string' || !Number.isFinite(Date.parse(candidate.publishedAt)) ||
      (candidate.related != null && (!Array.isArray(candidate.related) || !candidate.related.every((related) => {
        if (!related || typeof related !== 'object') return false;
        const record = related as { kind?: unknown; id?: unknown; label?: unknown };
        return ['player', 'match', 'entry', 'tag'].includes(String(record.kind)) && typeof record.id === 'string' && record.id.length > 0 && typeof record.label === 'string' && record.label.length > 0;
      })))) return false;
    try {
      if (new URL(candidate.sourceUrl).protocol !== 'https:') return false;
      const sourceUrls = candidate.sourceUrls;
      if (sourceUrls != null && (!Array.isArray(sourceUrls) || sourceUrls.length === 0 || new Set(sourceUrls).size !== sourceUrls.length ||
          !sourceUrls.every((url) => typeof url === 'string' && new URL(url).protocol === 'https:') || !sourceUrls.includes(candidate.sourceUrl))) return false;
      if (candidate.sources == null) {
        if (sourceUrls != null) return false;
      } else if (typeof candidate.sources !== 'number' || !Number.isInteger(candidate.sources) || candidate.sources <= 0 ||
          (candidate.sources === 1 ? sourceUrls != null && sourceUrls.length !== 1 : !Array.isArray(sourceUrls) || sourceUrls.length !== candidate.sources)) return false;
      const host = (url: string) => new URL(url).hostname.replace(/^www\./, '').toLowerCase();
      if (candidate.certainty === 'verified' && (!Array.isArray(sourceUrls) || sourceUrls.length < 2 || candidate.sources !== sourceUrls.length || new Set(sourceUrls.map(sourcePublisher)).size !== sourceUrls.length)) return false;
      if (candidate.certainty === 'official') {
        const official = (url: string) => ['arsenal.com', 'thefa.com', 'premierleague.com', 'uefa.com'].some((domain) => host(url) === domain || host(url).endsWith(`.${domain}`));
        if (!official(candidate.sourceUrl) || (Array.isArray(sourceUrls) && !sourceUrls.every(official))) return false;
      }
      return true;
    } catch { return false; }
  };
  return Boolean(
    today &&
      typeof today === 'object' &&
      typeof today.id === 'string' &&
      validDate(today.date) &&
      Number.isFinite(Date.parse(today.updatedAt)) &&
      validSource(today.lead) &&
      Array.isArray(today.lanes) && today.lanes.every(validSource) &&
      (today.changes == null || (Array.isArray(today.changes) && today.changes.every(validSource))) &&
      (today.more == null || (Array.isArray(today.more) && today.more.every(validSource))),
  );
}

export function mergeNewsSeed(live: NewsFeed, seed: NewsFeed): NewsFeed {
  let result: NewsFeed;
  if (!seed.today) {
    result = live;
  } else if (!hasTodayDispatch(live)) {
    result = { ...live, today: seed.today };
  } else {
    const liveAt = Date.parse(live.today!.updatedAt);
    const seedAt = Date.parse(seed.today.updatedAt);
    result = Number.isFinite(seedAt) && (!Number.isFinite(liveAt) || seedAt > liveAt)
      ? { ...live, today: seed.today }
      : live;
  }
  // Dedup items at the read site so every consumer receives a clean list.
  const items = dedupeNewsItems(result.items);
  return { ...result, items: items.length ? items : dedupeNewsItems(seed.items) };
}

export function mergeFixturesSeed(live: FixturesFeed, seed: FixturesFeed): FixturesFeed {
  // The Worker is the authority: generic fixture writes reject dynamic data,
  // while the truth importer records provider permission and immutable
  // revisions before this envelope can exist.
  const source = live.source;
  if (
    source?.rights === 'approved' &&
    source.publicFixtureCount === live.fixtures.length &&
    source.fixtureCount > 0 &&
    source.fixtureCount <= live.fixtures.length &&
    live.fixtures.length > 0
  ) {
    return live;
  }
  return seed.fixtures.length > 0 ? seed : live;
}

function mergePlayerStorySeed(live: PlayerStory | undefined, seed: PlayerStory | undefined): PlayerStory | undefined {
  if (!live) return seed;
  if (!seed) return live;
  return {
    ...seed,
    ...live,
    lede: live.lede || seed.lede,
    chapters: live.chapters?.length ? live.chapters : seed.chapters,
    timeline: live.timeline?.length ? live.timeline : seed.timeline,
    honours: live.honours?.length ? live.honours : seed.honours,
    terraceName: live.terraceName ?? seed.terraceName,
    factFile: {
      born: live.factFile?.born || seed.factFile.born,
      signed: live.factFile?.signed || seed.factFile.signed,
    },
  };
}

function stripSyntheticPlayerFields<T extends Player>(player: T): T {
  const {
    stats: _stats,
    seasons: _seasons,
    ratings: _ratings,
    form: _form,
    ...safe
  } = player;
  void _stats;
  void _seasons;
  void _ratings;
  void _form;
  return safe as T;
}

export function mergeSquadSeed(live: SquadFeed, seed: SquadFeed): SquadFeed {
  if (live?.season !== seed.season || live?.scope !== seed.scope || typeof live.checkedAt !== 'string' || !Number.isFinite(Date.parse(live.checkedAt)) ||
      !Array.isArray(live.sources) || live.sources.length === 0 || !live.sources.every((url) => {
        try { return new URL(url).protocol === 'https:'; } catch { return false; }
      }) || !Array.isArray(live.players)) return seed;
  const liveIds = new Set(live.players.map((p) => p.id));
  const seedById = new Map(seed.players.map((p) => [p.id, p]));
  const players = live.players.map((player) => {
    const seeded = seedById.get(player.id);
    if (!seeded) return stripSyntheticPlayerFields(player);
    return stripSyntheticPlayerFields({
      ...seeded,
      ...player,
      story: mergePlayerStorySeed(player.story, seeded.story),
      personId: player.personId ?? seeded.personId,
    });
  });
  for (const seeded of seed.players) {
    if (!liveIds.has(seeded.id)) players.push(stripSyntheticPlayerFields(seeded));
  }
  return { ...seed, ...live, players };
}

function hasPsgFinal(
  games: Array<{ opp?: string; comp?: string; stage?: string }> | undefined,
): boolean {
  return Boolean(games?.some((g) => /paris saint-germain/i.test(g.opp ?? '') && /final/i.test(`${g.stage ?? ''} ${g.comp ?? ''}`)));
}

function isVerifiedTitleSnapshot(lastSeason: ClubFeed['lastSeason']): boolean {
  return Boolean(
    lastSeason &&
      /2025\/26/.test(lastSeason.label) &&
      lastSeason.row.p === 38 &&
      lastSeason.row.w === 26 &&
      lastSeason.row.d === 7 &&
      lastSeason.row.l === 5 &&
      lastSeason.row.pts === 85,
  );
}

export function mergeClubSeed(live: ClubFeed, seed: ClubFeed): ClubFeed {
  const validThisDay = (value: ClubFeed['thisDay']): boolean => value == null || Object.entries(value).every(([date, card]) => {
    if (!/^\d{2}-\d{2}$/.test(date) || !card || typeof card !== 'object') return false;
    const parsed = new Date(`2000-${date}T00:00:00Z`);
    if (!Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(5, 10) !== date || !/^\d{4}$/.test(card.year) || !card.text.trim() || !card.sourceLabel.trim()) return false;
    try { return new URL(card.sourceUrl).protocol === 'https:'; } catch { return false; }
  });
  const safeLive = validThisDay(live.thisDay) ? live : { ...live, thisDay: seed.thisDay };
  if (!seed.lastSeason) return { ...seed, ...safeLive };
  if (!safeLive.lastSeason) return { ...seed, ...safeLive, lastSeason: seed.lastSeason };
  const liveLastSeason = safeLive.lastSeason;
  const seedLastSeason = seed.lastSeason;
  if (isVerifiedTitleSnapshot(seedLastSeason) && /2025\/26/.test(liveLastSeason.label)) {
    return {
      ...seed,
      ...safeLive,
      // The current title table is a verified bundled snapshot. Do not let an
      // older platform/cache envelope reintroduce inflated points or scorers.
      lastSeason: seedLastSeason,
      honoursLedger: seed.honoursLedger,
    };
  }
  const useSeedRunIn = hasPsgFinal(seedLastSeason.last10) && !hasPsgFinal(liveLastSeason.last10);
  const useSeedResults = hasPsgFinal(seedLastSeason.results) && !hasPsgFinal(liveLastSeason.results);
  return {
    ...seed,
    ...safeLive,
    lastSeason: {
      ...seedLastSeason,
      ...liveLastSeason,
      last10: useSeedRunIn ? seedLastSeason.last10 : liveLastSeason.last10,
      results: useSeedResults ? seedLastSeason.results : liveLastSeason.results,
    },
    honoursLedger: seed.honoursLedger,
  };
}

export function mergeMatchSeed(live: MatchFeed, seed: MatchFeed): MatchFeed {
  if (!validMatchShape(live)) return seed;

  // The Worker has already cross-validated this identity against the current
  // rights-gated fixture feed. Repeating that check against the bundled five
  // here would quarantine every later authorised match.
  // A valid live payload is its own publication authority. Optional editorial
  // fields that are absent must stay absent: spreading the bundled seed here
  // can leak preview, crowd or archive copy from a different match.
  return { ...live };
}

const record = (value: unknown): value is Record<string, unknown> => Boolean(value && typeof value === 'object' && !Array.isArray(value));
const textField = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;

function validFixturesShape(payload: unknown): payload is FixturesFeed {
  if (JSON.stringify(payload) === JSON.stringify(seedFixtures)) return true;
  if (!record(payload) || payload.productScope !== 'arsenal-men-first-team' || typeof payload.season !== 'string' || !Array.isArray(payload.fixtures) || payload.fixtures.length === 0) return false;
  if (!record(payload.editorial) || !['selected-matchbook', 'premier-league-complete', 'authorised-provider-snapshot'].includes(String(payload.editorial.coverage)) || !Number.isFinite(Date.parse(String(payload.editorial.checkedAt))) || typeof payload.editorial.officialCalendar !== 'string') return false;
  if (!record(payload.source) || payload.source.rights !== 'approved' || typeof payload.source.provider !== 'string' || !Number.isInteger(payload.source.fixtureCount) || Number(payload.source.fixtureCount) < 1 || Number(payload.source.fixtureCount) > payload.fixtures.length || Number(payload.source.publicFixtureCount) !== payload.fixtures.length || !Number.isFinite(Date.parse(String(payload.source.checkedAt)))) return false;
  if (payload.source.licenseUrl != null) {
    try { if (typeof payload.source.licenseUrl !== 'string' || new URL(payload.source.licenseUrl).protocol !== 'https:') return false; } catch { return false; }
  }
  if (payload.source.termsUrl != null) {
    try { if (typeof payload.source.termsUrl !== 'string' || new URL(payload.source.termsUrl).protocol !== 'https:') return false; } catch { return false; }
  }
  if (payload.source.sourceTimestampStatus != null && !['unknown', 'utc'].includes(String(payload.source.sourceTimestampStatus))) return false;
  if (payload.source.sourceTimestampStatus === 'unknown' && (payload.source.provider !== 'openligadb' || payload.source.url !== 'https://api.openligadb.de' || payload.source.contentUpdatedAt !== null)) return false;
  const ids = new Set<string>();
  return payload.fixtures.every((value) => {
    if (!record(value) || typeof value.id !== 'string' || ids.has(value.id)) return false;
    ids.add(value.id);
    const tbc = value.timeTbc === true;
    const kickoffOk = tbc
      ? value.kickoffUtc === null && typeof value.kickoffDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.kickoffDate)
      : typeof value.kickoffUtc === 'string' && Number.isFinite(Date.parse(value.kickoffUtc));
    return kickoffOk && typeof value.comp === 'string' && typeof value.opponent === 'string' && typeof value.opponentShort === 'string' &&
      ['H', 'A', 'N'].includes(String(value.venue)) && ['scheduled', 'live', 'ft', 'postponed'].includes(String(value.status)) && validScoreShape(value.score) &&
      (value.statusQuality == null || (value.statusQuality === 'overdue' && value.status === 'scheduled' && value.score == null));
  });
}

const validScoreShape = (value: unknown): boolean => value === null || Boolean(record(value) && Number.isInteger(value.home) && Number(value.home) >= 0 && Number(value.home) <= 99 && Number.isInteger(value.away) && Number(value.away) >= 0 && Number(value.away) <= 99);
const validEventShape = (value: unknown): boolean => record(value) && Number.isInteger(value.min) && Number(value.min) >= 0 && Number(value.min) <= 130 &&
  ['goal', 'own-goal', 'pen', 'red', 'yellow', 'sub', 'var', 'note'].includes(String(value.type)) &&
  (value.player == null || (typeof value.player === 'string' && value.player.length <= 100)) &&
  (value.detail == null || (typeof value.detail === 'string' && value.detail.length <= 500)) &&
  (value.ours == null || typeof value.ours === 'boolean');

function validCommunityReport(value: unknown, payload: Record<string, unknown>): value is CommunityReport {
  if (!record(value) || value.provider !== 'openligadb' || value.matchId !== payload.matchId ||
      typeof value.kickoffUtc !== 'string' || value.kickoffUtc !== payload.kickoffUtc || !Number.isFinite(Date.parse(value.kickoffUtc)) ||
      !record(value.score) || !validScoreShape(value.score) ||
      typeof value.checkedAt !== 'string' || !Number.isFinite(Date.parse(value.checkedAt)) ||
      value.sourceUrl !== 'https://api.openligadb.de' || value.licenseUrl !== 'https://www.openligadb.de/lizenz' ||
      (value.sourceUpdatedAt !== null && (typeof value.sourceUpdatedAt !== 'string' || !Number.isFinite(Date.parse(value.sourceUpdatedAt)))) ||
      value.sourceTimestampStatus !== 'unknown') return false;
  if (value.sourceTimestampStatus === 'unknown' && value.sourceUpdatedAt !== null) return false;
  return true;
}

export function validMatchShape(payload: unknown): payload is MatchFeed {
  if (!record(payload)) return false;
  const tbc = payload.timeTbc === true;
  const kickoffValid = tbc
    ? payload.kickoffUtc === null && typeof payload.kickoffDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(payload.kickoffDate) && Number.isFinite(Date.parse(`${payload.kickoffDate}T00:00:00Z`))
    : typeof payload.kickoffUtc === 'string' && Number.isFinite(Date.parse(payload.kickoffUtc)) && payload.kickoffDate == null;
  if (typeof payload.matchId !== 'string' || !/^[a-z0-9-]{3,100}$/.test(payload.matchId) || !kickoffValid ||
      !textField(payload.comp) || !textField(payload.opponent) || typeof payload.opponentShort !== 'string' || !/^[A-Z0-9]{2,6}$/.test(payload.opponentShort) ||
      !['H', 'A', 'N'].includes(String(payload.venue)) || (payload.venue === 'N' ? !['home', 'away'].includes(String(payload.arsenalSide)) : payload.arsenalSide != null) ||
      (payload.ground != null && typeof payload.ground !== 'string') || (payload.tv != null && typeof payload.tv !== 'string') || (payload.matchStatus != null && payload.matchStatus !== 'postponed') ||
      !['idle', 'pre', 'live', 'ht', 'ft'].includes(String(payload.phase)) || !validScoreShape(payload.score) ||
      (payload.minute !== null && (!Number.isInteger(payload.minute) || Number(payload.minute) < 0 || Number(payload.minute) > 130)) ||
      !Array.isArray(payload.events) || payload.events.length > 200 || !payload.events.every(validEventShape)) return false;
  if ((payload.phase === 'idle' || payload.phase === 'pre') && (payload.score !== null || payload.minute !== null || payload.events.length !== 0)) return false;
  if ((payload.phase === 'live' || payload.phase === 'ht') && payload.score === null) return false;
  if (payload.phase === 'ft' && payload.score === null) return false;
  if (payload.score != null) {
    const scoring = (payload.events as Array<Record<string, unknown>>).filter((event) => ['goal', 'own-goal', 'pen'].includes(String(event.type)));
    const arsenalHome = payload.venue === 'H' || (payload.venue === 'N' && payload.arsenalSide === 'home');
    const score = payload.score as Record<'home' | 'away', number>;
    const ours = Number(score[arsenalHome ? 'home' : 'away']);
    const theirs = Number(score[arsenalHome ? 'away' : 'home']);
    if (scoring.length > ours + theirs || scoring.filter((event) => event.ours === true).length > ours || scoring.filter((event) => event.ours === false).length > theirs) return false;
  }
  if (payload.lastResult != null) {
    if (!record(payload.lastResult)) return false;
    const lastResult = payload.lastResult;
    if (typeof lastResult.matchId !== 'string' || !textField(lastResult.label) || !textField(lastResult.comp) || !textField(lastResult.opponent) ||
        typeof lastResult.opponentShort !== 'string' || !['H', 'A', 'N'].includes(String(lastResult.venue)) ||
        (lastResult.venue === 'N' ? !['home', 'away'].includes(String(lastResult.arsenalSide)) : lastResult.arsenalSide != null) || !validScoreShape(lastResult.score) || lastResult.score === null ||
        typeof lastResult.playedAt !== 'string' || !Number.isFinite(Date.parse(lastResult.playedAt)) || Date.parse(lastResult.playedAt) >= Date.parse(typeof payload.kickoffUtc === 'string' ? payload.kickoffUtc : `${String(payload.kickoffDate)}T23:59:59Z`)) return false;
  }
  if (payload.communityReport != null && (!['idle', 'pre'].includes(String(payload.phase)) || payload.score !== null || payload.minute !== null || payload.events.length !== 0 || !validCommunityReport(payload.communityReport, payload))) return false;
  return payload.preview == null || (record(payload.preview) && textField(payload.preview.quote) && typeof payload.preview.confidence === 'number' && textField(payload.preview.keyBattle));
}

function validSquadShape(payload: unknown): payload is SquadFeed {
  if (!record(payload) || payload.productScope !== 'arsenal-men-first-team' || typeof payload.season !== 'string' ||
      typeof payload.scope !== 'string' || typeof payload.checkedAt !== 'string' || !Number.isFinite(Date.parse(payload.checkedAt)) ||
      !Array.isArray(payload.sources) || payload.sources.length === 0 || !payload.sources.every((value) => {
        try { return typeof value === 'string' && new URL(value).protocol === 'https:'; } catch { return false; }
      }) || !Array.isArray(payload.players)) return false;
  const ids = new Set<string>();
  return payload.players.every((value) => {
    if (!record(value) || typeof value.id !== 'string' || ids.has(value.id) || typeof value.name !== 'string' ||
        typeof value.full !== 'string' || typeof value.pos !== 'string' || typeof value.group !== 'string') return false;
    ids.add(value.id);
    return true;
  });
}

function validNewsShape(payload: unknown): payload is NewsFeed {
  return record(payload) && payload.productScope === 'arsenal-men-first-team' && Array.isArray(payload.items) && dedupeNewsItems(payload.items as NewsItem[]).length === payload.items.length && hasTodayDispatch(payload as unknown as NewsFeed);
}

function validClubShape(payload: unknown): payload is ClubFeed {
  if (!record(payload) || payload.productScope !== 'arsenal-men-first-team' || !Array.isArray(payload.trophies)) return false;
  const reviewed = seedClub as unknown as ClubFeed;
  if (JSON.stringify(payload.trophies) !== JSON.stringify(reviewed.trophies) ||
      JSON.stringify(payload.lastSeason ?? null) !== JSON.stringify(reviewed.lastSeason ?? null) ||
      JSON.stringify(payload.honoursLedger ?? null) !== JSON.stringify(reviewed.honoursLedger ?? null)) return false;
  if (payload.lastSeason != null && (!record(payload.lastSeason) || !record(payload.lastSeason.row) ||
      !Array.isArray(payload.lastSeason.headlineStats) || !Array.isArray(payload.lastSeason.last10) ||
      !Array.isArray(payload.lastSeason.keyMetrics) || !record(payload.lastSeason.scorers) ||
      !Array.isArray(payload.lastSeason.scorers.goals) || !Array.isArray(payload.lastSeason.scorers.assists) || !Array.isArray(payload.lastSeason.results))) return false;
  if (payload.honoursLedger != null && (!record(payload.honoursLedger) || !Array.isArray(payload.honoursLedger.categories))) return false;
  return true;
}

function validCodexShape(payload: unknown): payload is CodexFeed {
  return JSON.stringify(payload) === JSON.stringify(seedCodex);
}

function validLoreShape(payload: unknown): payload is LoreFeed {
  return JSON.stringify(payload) === JSON.stringify(seedLore);
}

function validGlossaryShape(payload: unknown): payload is GlossaryFeed {
  return JSON.stringify(payload) === JSON.stringify(seedGlossary);
}

export const FEEDS = {
  fixtures: { feed: 'fixtures', seed: seedFixtures as unknown as FixturesFeed, mergeSeed: mergeFixturesSeed, validate: validFixturesShape },
  match: { feed: 'match-live', seed: seedMatch as unknown as MatchFeed, mergeSeed: mergeMatchSeed, validate: validMatchShape },
  squad: { feed: 'squad', seed: seedSquad as unknown as SquadFeed, mergeSeed: mergeSquadSeed, validate: validSquadShape },
  news: { feed: 'news', seed: seedNews as unknown as NewsFeed, mergeSeed: mergeNewsSeed, validate: validNewsShape },
  club: { feed: 'club', seed: seedClub as unknown as ClubFeed, mergeSeed: mergeClubSeed, validate: validClubShape },
  codex: { feed: 'codex', seed: seedCodex as unknown as CodexFeed, validate: validCodexShape },
  lore: { feed: 'lore', seed: seedLore as unknown as LoreFeed, validate: validLoreShape },
  glossary: { feed: 'glossary', seed: seedGlossary as unknown as GlossaryFeed, validate: validGlossaryShape },
} as const;

function isStale(env: FeedEnvelope, nowMs: number): boolean {
  if (!env.staleAfter) return false;
  const t = Date.parse(env.staleAfter);
  return Number.isFinite(t) && nowMs > t;
}

/**
 * Content freshness is stricter than transport freshness. An envelope can be
 * newly republished while its evidence is still old; keep that distinction
 * visible to every consumer.
 */
export function contentIsStale(feed: string, payload: unknown, nowMs = Date.now()): boolean {
  if (!payload || typeof payload !== 'object') return true;
  const record = payload as Record<string, unknown>;
  if (feed === 'news') {
    const today = record.today;
    return !today || typeof today !== 'object' || (today as Record<string, unknown>).date !== londonCalendarDate(nowMs);
  }
  if (feed === 'squad') {
    const checkedAt = typeof record.checkedAt === 'string' ? Date.parse(record.checkedAt) : Number.NaN;
    // Squad evidence has a three-day operational window. This is based on
    // the source review instant, never on when a payload was republished.
    return !Number.isFinite(checkedAt) || nowMs - checkedAt > 3 * 86_400_000;
  }
  return false;
}

function fromEnvelope<T>(env: FeedEnvelope<T> | null, spec: SeedSpec<T>, online: boolean): FeedState<T> {
  if (!env) return { data: spec.seed, asOf: null, stale: true, online: false };
  return {
    data: spec.mergeSeed ? spec.mergeSeed(env.payload, spec.seed) : env.payload,
    asOf: env.updatedAt,
    stale: !online || isStale(env, Date.now()) || contentIsStale(spec.feed, env.payload),
    online,
  };
}

const missingFeeds = new Set<string>();
const pendingRefreshes = new Map<string, Promise<FeedState<unknown>>>();

/** Synchronous best snapshot for first paint: cache if present, else seed. */
export function initialFeedState<T>(spec: SeedSpec<T>): FeedState<T> {
  try {
    const cached = cachedFeed<T>(spec);
    return fromEnvelope(cached, spec, false);
  } catch {
    return { data: spec.seed, asOf: null, stale: true, online: false };
  }
}

export async function refreshFeed<T>(spec: SeedSpec<T>): Promise<FeedState<T>> {
  // A prior 404 is only a snapshot, never a tab-lifetime verdict. Clear the
  // negative marker before the next explicit retry so first-install feed
  // publication and transient deploy gaps recover without a reload.
  missingFeeds.delete(spec.feed);

  const pending = pendingRefreshes.get(spec.feed);
  if (pending) return pending as Promise<FeedState<T>>;

  const refresh = (async () => {
    try {
      const env = await getFeed<T>(spec);
      if (env) return fromEnvelope(env, spec, true);
      missingFeeds.add(spec.feed);
    } catch (error) {
      if (error instanceof ReadUnavailable) throw error;
      if (error instanceof TypeError || (error instanceof DOMException && ['AbortError', 'TimeoutError'].includes(error.name))) throw new ReadUnavailable(0);
      /* malformed publication — use the reviewed fallback */
    } finally {
      pendingRefreshes.delete(spec.feed);
    }
    return initialFeedState(spec);
  })();

  pendingRefreshes.set(spec.feed, refresh as Promise<FeedState<unknown>>);
  return refresh;
}

export function resetFeedSessionState(): void {
  missingFeeds.clear();
  pendingRefreshes.clear();
  resetPublicReads();
}

export function isFeedMarkedMissing(feed: string): boolean {
  return missingFeeds.has(feed);
}

/** Match polling is bounded to the real kick-off window, never the stale phase. */
export function feedPollMs(feed: string, payload: unknown, now = Date.now()): number {
  if (feed === 'news') return 5 * 60_000;
  if (feed === 'fixtures') return 15 * 60_000;
  if (feed === 'match-live') {
    const match = payload as MatchFeed;
    const kickoff = match?.kickoffUtc ? Date.parse(match.kickoffUtc) : Number.NaN;
    return Number.isFinite(kickoff) && now >= kickoff - 15 * 60_000 && now <= kickoff + 4 * 60 * 60_000 ? 60_000 : 15 * 60_000;
  }
  return 60 * 60_000;
}

/** Shared TTL prevents duplicate App/screen requests and refreshes on tab focus. */
export function useFeed<T>(spec: SeedSpec<T>, _pollMs?: number): FeedState<T> {
  return usePublicRead({
    key: `feed:${spec.feed}`,
    initial: () => initialFeedState(spec),
    load: () => refreshFeed(spec),
    interval: (state) => feedPollMs(spec.feed, state.data),
    onError: (state) => RIGHTS_GATED_FEEDS.has(spec.feed)
      ? initialFeedState(spec)
      : { ...state, stale: true, online: false },
  });
}

/** Compact "as of" label for stale badges: "as of 14:05" / "as of Sat 16 Aug". */
export function asOfLabel(asOf: string | null): string {
  if (!asOf) return 'Offline · saved copy';
  const t = Date.parse(asOf);
  if (!Number.isFinite(t)) return 'Saved copy';
  const d = new Date(t);
  const sameDay = new Date().toDateString() === d.toDateString();
  if (sameDay) {
    return `as of ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  return `as of ${d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}`;
}
